import { type Storage, createStorage } from 'unstorage'
import indexedDbDriver from 'unstorage/drivers/indexedb'
import { sha1 } from './helpers'

export type RequestCacheItem<T> = {
  timestamp: number
  data: T
}

export class RequestCache<T = any> {
  #duration: number
  #storage: Storage<RequestCacheItem<T>>

  constructor({
    duration = -1,
    namespace = 'person-filter:',
  }: {
    duration?: number
    namespace?: string
  }) {
    this.#duration = duration
    this.#storage = createStorage({
      driver: indexedDbDriver({ base: namespace }),
    })
  }

  async makeKey(url: string, body: BodyInit | null | undefined = undefined) {
    return await sha1(
      JSON.stringify({
        url,
        body,
      }),
    )
  }

  #isValid(item: RequestCacheItem<T>) {
    if (this.#duration === -1) return true
    return Date.now() - item.timestamp < this.#duration
  }

  async #validate(key: string, item: RequestCacheItem<T>) {
    if (!this.#isValid(item)) {
      await this.#storage.removeItem(key)
      return false
    }

    return true
  }

  async has(key: string): Promise<boolean> {
    const hasItem = await this.#storage.hasItem(key)
    if (!hasItem) return false

    const item = await this.#storage.getItem(key)

    if (item === null) return false
    if (!(await this.#validate(key, item))) return false
    return true
  }

  async get(key: string): Promise<T | null> {
    const item = await this.#storage.getItem(key)

    if (item === null) return null
    if (!(await this.#validate(key, item))) return null

    return item.data
  }

  async set(key: string, data: T): Promise<void> {
    await this.#storage.setItem(key, {
      timestamp: Date.now(),
      data,
    })
  }

  async remove(key: string): Promise<void> {
    await this.#storage.removeItem(key)
  }

  async clear() {
    await this.#storage.clear()
  }
}

let caches = new Map<string, any>()
export function getCache<T = any>(
  options: {
    duration?: number
    namespace?: string
  } = {},
): RequestCache<T> {
  const sortedOptions = Object.fromEntries(
    Object.entries(options).sort(([keyA], [keyB]) => keyA.localeCompare(keyB)),
  )
  const key = JSON.stringify(sortedOptions)
  if (!caches.has(key)) {
    caches.set(key, new RequestCache(options))
  }
  return caches.get(key)
}
